import React, { useEffect, useState } from 'react';
import { Save, CreditCard, Building2, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle, TestTube, Rocket } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

const PaymentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({
    razorpay: false,
    razorpay_test: false,
    stripe: false,
    stripe_test: false
  });
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    test_mode: true,  // Default to test mode for safety
    
    razorpay_enabled: false,
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_test_key_id: '',
    razorpay_test_key_secret: '',
    
    stripe_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_test_publishable_key: '',
    stripe_test_secret_key: '',
    
    manual_enabled: true,
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder: '',
    upi_id: '',
    upi_qr_image: '',
    
    payment_instructions: 'Please complete the payment and upload the screenshot for verification.'
  });

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/shop-payments/settings');
      setFormData(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/shop-payments/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/admin/shop-payments/settings', formData);
      toast.success('Payment settings saved successfully!');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const maskSecret = (secret) => {
    if (!secret) return '';
    if (secret.length <= 8) return '••••••••';
    return secret.substring(0, 4) + '••••••••' + secret.substring(secret.length - 4);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading payment settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="payment-settings-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="payment-settings-title">
            <CreditCard className="inline w-8 h-8 mr-3" />
            Payment Gateway Settings
          </h1>
          <button
            onClick={() => { fetchSettings(); fetchStats(); }}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-primary">{stats.total_orders}</p>
              <p className="text-sm text-muted-foreground">Total Orders</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-green-500">{stats.completed_orders}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-yellow-500">{stats.pending_verification}</p>
              <p className="text-sm text-muted-foreground">Pending Verification</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-primary">₹{(stats.total_revenue || 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Test Mode Toggle */}
          <div className={`glass-card p-6 border-2 ${formData.test_mode ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-green-500/50 bg-green-500/5'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {formData.test_mode ? (
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                    <TestTube className="w-6 h-6 text-yellow-500" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-green-500" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold">
                    {formData.test_mode ? 'Test Mode' : 'Live Mode'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {formData.test_mode 
                      ? 'Using sandbox keys - no real payments will be processed' 
                      : 'Using live keys - real payments will be charged'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, test_mode: !formData.test_mode })}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  formData.test_mode 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                }`}
                data-testid="toggle-mode-btn"
              >
                {formData.test_mode ? 'Switch to Live' : 'Switch to Test'}
              </button>
            </div>
            {!formData.test_mode && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span><strong>Warning:</strong> Live mode is active. All payments will be real and charged to customers.</span>
              </div>
            )}
          </div>

          {/* Razorpay Settings */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold text-blue-400">R</span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Razorpay</h2>
                  <p className="text-sm text-muted-foreground">UPI, Cards, Net Banking (India)</p>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.razorpay_enabled}
                  onChange={(e) => setFormData({ ...formData, razorpay_enabled: e.target.checked })}
                  className="w-5 h-5 rounded"
                  data-testid="razorpay-enabled"
                />
                <span className="text-sm font-medium">Enable</span>
              </label>
            </div>
            
            {formData.razorpay_enabled && (
              <div className="space-y-6 pt-4 border-t border-white/10">
                {/* Test Keys */}
                <div className={`p-4 rounded-xl border ${formData.test_mode ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-white/10 opacity-60'}`}>
                  <h3 className="text-sm font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                    <TestTube className="w-4 h-4" />
                    Test Keys {formData.test_mode && <span className="text-xs bg-yellow-500/20 px-2 py-0.5 rounded">ACTIVE</span>}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Test Key ID</label>
                      <input
                        type="text"
                        value={formData.razorpay_test_key_id}
                        onChange={(e) => setFormData({ ...formData, razorpay_test_key_id: e.target.value })}
                        placeholder="rzp_test_xxxxxxxxxxxxxxx"
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
                        data-testid="razorpay-test-key-id"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Test Key Secret</label>
                      <div className="relative">
                        <input
                          type={showSecrets.razorpay_test ? "text" : "password"}
                          value={formData.razorpay_test_key_secret}
                          onChange={(e) => setFormData({ ...formData, razorpay_test_key_secret: e.target.value })}
                          placeholder="Test secret key"
                          className="w-full px-3 py-2 pr-10 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
                          data-testid="razorpay-test-key-secret"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(prev => ({ ...prev, razorpay_test: !prev.razorpay_test }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                        >
                          {showSecrets.razorpay_test ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Keys */}
                <div className={`p-4 rounded-xl border ${!formData.test_mode ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 opacity-60'}`}>
                  <h3 className="text-sm font-semibold text-green-500 mb-3 flex items-center gap-2">
                    <Rocket className="w-4 h-4" />
                    Live Keys {!formData.test_mode && <span className="text-xs bg-green-500/20 px-2 py-0.5 rounded">ACTIVE</span>}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Live Key ID</label>
                      <input
                        type="text"
                        value={formData.razorpay_key_id}
                        onChange={(e) => setFormData({ ...formData, razorpay_key_id: e.target.value })}
                        placeholder="rzp_live_xxxxxxxxxxxxxxx"
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                        data-testid="razorpay-key-id"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Live Key Secret</label>
                      <div className="relative">
                        <input
                          type={showSecrets.razorpay ? "text" : "password"}
                          value={formData.razorpay_key_secret}
                          onChange={(e) => setFormData({ ...formData, razorpay_key_secret: e.target.value })}
                          placeholder="Live secret key"
                          className="w-full px-3 py-2 pr-10 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                          data-testid="razorpay-key-secret"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(prev => ({ ...prev, razorpay: !prev.razorpay }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                        >
                          {showSecrets.razorpay ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-blue-500/10 rounded-lg text-sm">
                  <AlertCircle className="inline w-4 h-4 mr-2" />
                  Get your Razorpay API keys from{' '}
                  <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                    Razorpay Dashboard → Settings → API Keys
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Stripe Settings */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold text-purple-400">S</span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Stripe</h2>
                  <p className="text-sm text-muted-foreground">International Cards</p>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.stripe_enabled}
                  onChange={(e) => setFormData({ ...formData, stripe_enabled: e.target.checked })}
                  className="w-5 h-5 rounded"
                  data-testid="stripe-enabled"
                />
                <span className="text-sm font-medium">Enable</span>
              </label>
            </div>
            
            {formData.stripe_enabled && (
              <div className="space-y-6 pt-4 border-t border-white/10">
                {/* Test Keys */}
                <div className={`p-4 rounded-xl border ${formData.test_mode ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-white/10 opacity-60'}`}>
                  <h3 className="text-sm font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                    <TestTube className="w-4 h-4" />
                    Test Keys {formData.test_mode && <span className="text-xs bg-yellow-500/20 px-2 py-0.5 rounded">ACTIVE</span>}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Test Publishable Key</label>
                      <input
                        type="text"
                        value={formData.stripe_test_publishable_key}
                        onChange={(e) => setFormData({ ...formData, stripe_test_publishable_key: e.target.value })}
                        placeholder="pk_test_xxxxxxxxxxxxxxx"
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
                        data-testid="stripe-test-publishable-key"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Test Secret Key</label>
                      <div className="relative">
                        <input
                          type={showSecrets.stripe_test ? "text" : "password"}
                          value={formData.stripe_test_secret_key}
                          onChange={(e) => setFormData({ ...formData, stripe_test_secret_key: e.target.value })}
                          placeholder="sk_test_xxxxxxxxxxxxxxx"
                          className="w-full px-3 py-2 pr-10 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
                          data-testid="stripe-test-secret-key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(prev => ({ ...prev, stripe_test: !prev.stripe_test }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                        >
                          {showSecrets.stripe_test ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Keys */}
                <div className={`p-4 rounded-xl border ${!formData.test_mode ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 opacity-60'}`}>
                  <h3 className="text-sm font-semibold text-green-500 mb-3 flex items-center gap-2">
                    <Rocket className="w-4 h-4" />
                    Live Keys {!formData.test_mode && <span className="text-xs bg-green-500/20 px-2 py-0.5 rounded">ACTIVE</span>}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Live Publishable Key</label>
                      <input
                        type="text"
                        value={formData.stripe_publishable_key}
                        onChange={(e) => setFormData({ ...formData, stripe_publishable_key: e.target.value })}
                        placeholder="pk_live_xxxxxxxxxxxxxxx"
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                        data-testid="stripe-publishable-key"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Live Secret Key</label>
                      <div className="relative">
                        <input
                          type={showSecrets.stripe ? "text" : "password"}
                          value={formData.stripe_secret_key}
                          onChange={(e) => setFormData({ ...formData, stripe_secret_key: e.target.value })}
                          placeholder="sk_live_xxxxxxxxxxxxxxx"
                          className="w-full px-3 py-2 pr-10 text-sm bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                          data-testid="stripe-secret-key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets(prev => ({ ...prev, stripe: !prev.stripe }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                        >
                          {showSecrets.stripe ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-purple-500/10 rounded-lg text-sm">
                  <AlertCircle className="inline w-4 h-4 mr-2" />
                  Get your Stripe API keys from{' '}
                  <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                    Stripe Dashboard → Developers → API Keys
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Manual Payment Settings */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Manual Payment</h2>
                  <p className="text-sm text-muted-foreground">Bank Transfer / UPI</p>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.manual_enabled}
                  onChange={(e) => setFormData({ ...formData, manual_enabled: e.target.checked })}
                  className="w-5 h-5 rounded"
                  data-testid="manual-enabled"
                />
                <span className="text-sm font-medium">Enable</span>
              </label>
            </div>
            
            {formData.manual_enabled && (
              <div className="space-y-4 pt-4 border-t border-white/10">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="State Bank of India"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="bank-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Holder Name</label>
                    <input
                      type="text"
                      value={formData.account_holder}
                      onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                      placeholder="ANANT ENTERPRISES"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="account-holder"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Number</label>
                    <input
                      type="text"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      placeholder="1234567890123456"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="account-number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">IFSC Code</label>
                    <input
                      type="text"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })}
                      placeholder="SBIN0001234"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="ifsc-code"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">UPI ID</label>
                    <input
                      type="text"
                      value={formData.upi_id}
                      onChange={(e) => setFormData({ ...formData, upi_id: e.target.value })}
                      placeholder="anant@upi"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="upi-id"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">UPI QR Code URL</label>
                    <input
                      type="url"
                      value={formData.upi_qr_image}
                      onChange={(e) => setFormData({ ...formData, upi_qr_image: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                      data-testid="upi-qr-image"
                    />
                  </div>
                </div>

                {formData.upi_qr_image && (
                  <div className="flex justify-center">
                    <div className="p-4 bg-white rounded-xl">
                      <img src={getImageUrl(formData.upi_qr_image)} alt="UPI QR" className="w-40 h-40 object-contain" />
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">Payment Instructions</label>
                  <textarea
                    value={formData.payment_instructions}
                    onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                    rows={3}
                    placeholder="Instructions shown to customers for manual payment..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50"
                    data-testid="payment-instructions"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Status Summary */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Active Payment Methods</h3>
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${formData.test_mode ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}>
                {formData.test_mode ? 'TEST MODE' : 'LIVE MODE'}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {formData.razorpay_enabled && (formData.test_mode ? formData.razorpay_test_key_id : formData.razorpay_key_id) && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 rounded-lg text-blue-400">
                  <CheckCircle className="w-4 h-4" />
                  Razorpay {formData.test_mode && <span className="text-xs opacity-70">(test)</span>}
                </div>
              )}
              {formData.stripe_enabled && (formData.test_mode ? formData.stripe_test_publishable_key : formData.stripe_publishable_key) && (
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 rounded-lg text-purple-400">
                  <CheckCircle className="w-4 h-4" />
                  Stripe {formData.test_mode && <span className="text-xs opacity-70">(test)</span>}
                </div>
              )}
              {formData.manual_enabled && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 rounded-lg text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  Manual Payment
                </div>
              )}
              {!formData.razorpay_enabled && !formData.stripe_enabled && !formData.manual_enabled && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 rounded-lg text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  No payment methods active
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="save-payment-settings"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Payment Settings'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PaymentSettings;
